import { useCallback, useEffect, useRef, useState } from "react";
import { GetQuestionnaireResponse } from "@/services/fhir/questionnaireresponse/GetQuestionnaries";
import type { ClinicalInsightDateRange } from "src-next/modules/clinical-insights/types/analysisTypes";
import type { LoadPhase, PatientsProgress } from "src-next/modules/clinical-insights/loadPhase";
import { fetchPatientsParallel } from "src-next/modules/clinical-insights/helpers/fetchPatientsParallel";

interface UseQrClinicalRegisterAnalysisArgs<T> {
  author: string;
  practitionerFilter: string;
  dateRange?: ClinicalInsightDateRange;
  getPractitionerName: (ref: string) => string;
  onLoadPhaseChange?: (phase: LoadPhase) => void;
  processEntries: (entries: unknown[], getPractitionerName: (ref: string) => string) => T[];
  getPatientIds: (records: T[]) => string[];
  /** Quando false, não busca nem propaga load phase (card oculto). */
  fetchEnabled?: boolean;
}

export function useQrClinicalRegisterAnalysis<T>({
  author,
  practitionerFilter,
  dateRange,
  getPractitionerName,
  onLoadPhaseChange,
  processEntries,
  getPatientIds,
  fetchEnabled = true,
}: UseQrClinicalRegisterAnalysisArgs<T>) {
  const [records, setRecords] = useState<T[]>([]);
  const [patients, setPatients] = useState<Map<string, fhir5.Patient>>(new Map());
  const [loadPhase, setLoadPhase] = useState<LoadPhase>("idle");
  const [patientsProgress, setPatientsProgress] = useState<PatientsProgress>({
    loaded: 0,
    total: 0,
  });
  const requestIdRef = useRef(0);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const processEntriesRef = useRef(processEntries);
  const getPatientIdsRef = useRef(getPatientIds);
  const getPractitionerNameRef = useRef(getPractitionerName);
  const fetchEnabledRef = useRef(fetchEnabled);
  fetchEnabledRef.current = fetchEnabled;
  const onLoadPhaseChangeRef = useRef(onLoadPhaseChange);
  onLoadPhaseChangeRef.current = onLoadPhaseChange;

  useEffect(() => {
    processEntriesRef.current = processEntries;
    getPatientIdsRef.current = getPatientIds;
    getPractitionerNameRef.current = getPractitionerName;
  }, [processEntries, getPatientIds, getPractitionerName]);

  const setPhase = useCallback((phase: LoadPhase) => {
    setLoadPhase(phase);
    if (fetchEnabledRef.current) onLoadPhaseChangeRef.current?.(phase);
  }, []);

  const fetchData = useCallback(async () => {
    if (!author) return;

    const requestId = ++requestIdRef.current;
    setPhase("records");

    try {
      const response = await GetQuestionnaireResponse({
        author,
        practitioner: practitionerFilter,
        hasProtocols: false,
        questionnaire: "Questionnaire/clinicalRegister",
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
      });

      if (requestId !== requestIdRef.current) return;

      const entries: unknown[] = response?.entry || [];
      const parsed = processEntriesRef.current(
        entries,
        getPractitionerNameRef.current
      );
      setRecords(parsed);
      setHasLoadedOnce(true);

      const uniquePatientIds = Array.from(
        new Set(getPatientIdsRef.current(parsed))
      );
      if (uniquePatientIds.length === 0) {
        setPatients(new Map());
        setPatientsProgress({ loaded: 0, total: 0 });
        setPhase("done");
        return;
      }

      setPhase("patients");
      const patientMap = await fetchPatientsParallel(uniquePatientIds, {
        requestId,
        isRequestCurrent: (id) => id === requestIdRef.current,
        onProgress: (loaded, total) => {
          if (requestId === requestIdRef.current) {
            setPatientsProgress({ loaded, total });
          }
        },
      });

      if (requestId === requestIdRef.current) {
        setPatients(patientMap);
        setPhase("done");
      }
    } catch (error) {
      console.error("Erro ao buscar registros clínicos:", error);
      if (requestId === requestIdRef.current) {
        setHasLoadedOnce(true);
        setPhase("error");
      }
    }
  }, [author, practitionerFilter, dateRange?.startDate, dateRange?.endDate, setPhase]);

  useEffect(() => {
    if (author && fetchEnabled) fetchData();
  }, [fetchData, author, fetchEnabled]);

  return {
    records,
    patients,
    loadPhase,
    patientsProgress,
    hasData: hasLoadedOnce || records.length > 0,
    retry: fetchData,
  };
}
